import React, { useState, useMemo, memo } from "react";
import { useSelector, useDispatch } from "react-redux";
import PropTypes from "prop-types";
import { Table, Checkbox, Input, Header } from "semantic-ui-react";
import _ from "lodash";
import uuid from "uuid/v4";
import {
  setAreaDoneState,
  setAreasDoneState,
  setAllAreasDoneState,
  setAreaValue,
  updateAreasList
} from "../../../../../actions/SprayDiary/areas";
import { setSprayDiaryField } from "../../../../../actions/SprayDiary/common";

import styles from "./SelectedAreasTable.module.css";
import { calcTotalHectare } from "../../../../../utils/tasksUtils";
import { matterToUnits } from "utils/constToUnits";
import { calculateChemicalQuantity } from "utils/chemicalBreakdownUtils";
import Numeric from "../../../../../components/Numeric";
import { Area } from "../../../../../models/block.model";
import { parentBlocksSelector } from "selectors/sprayDiary";
import { unitsSelector } from "selectors/chemicals";

const SelectedAreasTable = () => {
  const dispatch = useDispatch();
  const {
    applicationSettings: { showParentBlocks },
    sprayDiary: {
      selectedAreas,
      selectedMachinery: machinery,
      totalTanks,
      actualTotalTanks, // ?
      plannedDate,
      selectedChemicals,
      rowsToSpray,
      widthPerRow,
      literPerHectare,
      conc
    }
  } = useSelector(state => state);
  const parentBlocks = useSelector(parentBlocksSelector);
  const [usedVolumeKey, setUsedVolumeKey] = useState();
  const [totalUsedVolumeKey, setTotalUsedVolumeKey] = useState();
  const [usedTanksKey, setUsedTanksKey] = useState();
  const [totalUsedTanksKey, setTotalUsedTanksKey] = useState();
  const allDone = selectedAreas.every(area => area.done);
  const someDone = selectedAreas.some(area => area.done);
  const totalHectares = calcTotalHectare(selectedAreas);
  const selectedMachinery = machinery && machinery.id ? machinery : null;
  const machineryLitersSize = selectedMachinery ? selectedMachinery.size : 0;

  const { content, isFetching } = useSelector(unitsSelector);
  const multiplier = item => {
    const foundElement = content.find(({ id }) => id === item);

    if (foundElement) {
      return 1 / foundElement.multiplicationFactor;
    }

    return 1;
  };

  const determineShouldSetActualValue = actualValue =>
    actualValue !== "" &&
    actualValue !== null &&
    actualValue !== undefined &&
    !isNaN(actualValue);

  const {
    totalVolume,
    totalUsedVolume,
    totalUsedTanks,
    totalChemicals
  } = useMemo(() => {
    const initialValues = {
      totalVolume: 0,
      totalUsedVolume: 0,
      totalUsedTanks: 0
    };

    if (!selectedAreas) {
      return initialValues;
    }

    const result = selectedAreas.reduce((prev, curr) => {
      const areaTank = determineShouldSetActualValue(curr.actualTanks)
        ? parseFloat(curr.actualTanks)
        : curr.tanks;

      return {
        totalVolume: prev.totalVolume + curr.plannedLiters,
        totalUsedVolume: prev.totalUsedVolume + areaTank * machineryLitersSize,
        totalUsedTanks: prev.totalUsedTanks + areaTank
      };
    }, initialValues);

    return {
      totalVolume: parseFloat(result.totalVolume).toFixed(2),
      totalUsedVolume: parseFloat(result.totalUsedVolume).toFixed(2),
      totalUsedTanks: parseFloat(result.totalUsedTanks).toFixed(2),
      totalChemicals: selectedChemicals.map(chemical => ({
        units: chemical.rate.unit,
        quantity: selectedAreas.reduce((prev, { size, actualTanks, tanks }) => {
          const { quantity: predictedQuantity } = calculateChemicalQuantity(
            chemical,
            size,
            rowsToSpray,
            widthPerRow,
            literPerHectare,
            conc
          );

          let quantity = predictedQuantity;

          if (
            actualTanks !== null &&
            actualTanks !== undefined &&
            actualTanks !== tanks &&
            actualTanks > 0 &&
            tanks > 0
          ) {
            quantity = (predictedQuantity * actualTanks) / tanks;
          }

          return prev + quantity;
        }, 0)
      }))
    };
  }, [selectedAreas, selectedChemicals]);

  if (!selectedAreas.length) return null;

  const arrayOfAllParentBlocks = Object.values(parentBlocks);
  const listOfBlocks = {};
  const selectedAreasIndexed = Object.fromEntries(
    selectedAreas.map(area => [area.id, area])
  );

  arrayOfAllParentBlocks.forEach(area => {
    const selectedPatchesIDs = _.intersection(
      area.areaIDs,
      selectedAreas.map(area => area.id)
    );
    if (
      !_.isEmpty(area.areaIDs) &&
      _.isEqual(area.areaIDs, selectedPatchesIDs)
    ) {
      listOfBlocks[area.id] = {
        id: area.id,
        name: area.name,
        actualTanks: area.areaIDs
          .map(id => selectedAreasIndexed[id].actualTanks)
          .reduce((a, b) => parseFloat(a) + parseFloat(b), 0),
        size: area.areaSize.reduce((a, b) => a + b, 0),
        areaIDs: area.areaIDs,
        varietiesList: [],
        plannedLiters: 0,
        done: selectedAreas
          .filter(a => selectedPatchesIDs.includes(a.id))
          .every(a => a.done),
        tanks: 0
      };
    }
  });

  const arrayOfBlocks = Object.values(listOfBlocks);

  arrayOfBlocks.map(b => {
    b.areaIDs.forEach(id => {
      listOfBlocks[b.id].plannedLiters +=
        selectedAreasIndexed[id].plannedLiters;
      listOfBlocks[b.id].tanks += selectedAreasIndexed[id].tanks;
      listOfBlocks[b.id].varietiesList.push(
        `${selectedAreasIndexed[id].varieties.map(
          v => v.variety.name
        )} - ${selectedAreasIndexed[id].varieties.map(
          v => v.variety.crop.name
        )}`
      );
    });
  });

  const areasToShow = (() => {
    const filteredSelectedAreas = { ...selectedAreasIndexed };
    if (arrayOfBlocks.length >= 1) {
      arrayOfBlocks.map(block => {
        block.areaIDs.map(id => {
          delete filteredSelectedAreas[id];
        });
      });
    }
    return Object.values(filteredSelectedAreas).concat(arrayOfBlocks);
  })();

  const areasToMap = showParentBlocks ? selectedAreas : areasToShow;
  const totalUsedTanksDecimal = totalUsedTanks ? totalUsedTanks % 1 : 0;

  const indexedAreasToShow = Object.fromEntries(
    areasToShow.map(area => [area.id, area])
  );

  const handleAreaDoneClick = areaId => () => {
    if (showParentBlocks) {
      dispatch(setAreaDoneState(areaId));
    } else {
      const { areaIDs, done } = indexedAreasToShow[areaId] || {};

      areaIDs && areaIDs.length
        ? dispatch(setAreasDoneState(areaIDs, done))
        : dispatch(setAreaDoneState(areaId));
    }
  };

  const setActualTanks = (id, value) => {
    if (
      indexedAreasToShow[id] &&
      indexedAreasToShow[id].areaIDs &&
      indexedAreasToShow[id].areaIDs.length
    ) {
      indexedAreasToShow[id].areaIDs.forEach(patchId => {
        dispatch(
          setAreaValue(patchId, {
            actualTanks:
              value.actualTanks / indexedAreasToShow[id].areaIDs.length
          })
        );
      });
    } else {
      dispatch(setAreaValue(id, value));
    }

    dispatch(
      setSprayDiaryField({
        fieldName: "actualTotalTanks",
        fieldValue: null
      })
    );
  };

  const handleUsedVolumeChange = (area, actualVolumeValue) => {
    setActualTanks(area.id, {
      actualTanks: determineShouldSetActualValue(actualVolumeValue)
        ? +actualVolumeValue / machineryLitersSize
        : +area.tanks
    });
    setUsedTanksKey(uuid());
    setTotalUsedVolumeKey(uuid());
    setTotalUsedTanksKey(uuid());
  };

  const handleUsedTanksChange = (area, actualTanksValue) => {
    setActualTanks(area.id, {
      actualTanks: determineShouldSetActualValue(actualTanksValue)
        ? +actualTanksValue
        : +area.tanks
    });
    setUsedVolumeKey(uuid());
    setTotalUsedVolumeKey(uuid());
    setTotalUsedTanksKey(uuid());
  };

  const handleTotalUsedVolumeChange = actualTotalVolumeValue => {
    setTotalUsedTanksKey(uuid());
    setUsedTanksKey(uuid());
    setUsedVolumeKey(uuid());
    const shouldSetActualValue = determineShouldSetActualValue(
      actualTotalVolumeValue
    );
    const currentTotalVolumeValue = shouldSetActualValue
      ? actualTotalVolumeValue
      : totalVolume;

    const volumeFactor = currentTotalVolumeValue / totalVolume;

    const areasWithUpdatedActualTanks = areasToMap.map(area => {
      return {
        ...area,
        actualTanks: shouldSetActualValue
          ? (area.plannedLiters * volumeFactor) / machineryLitersSize
          : area.tanks
      };
    });

    dispatch(updateAreasList(areasWithUpdatedActualTanks));
    dispatch(
      setSprayDiaryField({
        fieldName: "actualTotalTanks",
        fieldValue: currentTotalVolumeValue / machineryLitersSize
      })
    );
  };

  const handleTotalUsedTanksChange = actualTotalTanksValue => {
    setTotalUsedVolumeKey(uuid());
    setUsedTanksKey(uuid());
    setUsedVolumeKey(uuid());
    const shouldSetActualValue = determineShouldSetActualValue(
      actualTotalTanksValue
    );
    const currentTotalTanksValue = shouldSetActualValue
      ? actualTotalTanksValue
      : totalTanks;

    const tanksFactor = currentTotalTanksValue / totalTanks;

    const areasWithUpdatedActualTanks = areasToMap.map(area => {
      return {
        ...area,
        actualTanks: shouldSetActualValue
          ? area.tanks * tanksFactor
          : area.tanks
      };
    });

    dispatch(updateAreasList(areasWithUpdatedActualTanks));
    dispatch(
      setSprayDiaryField({
        fieldName: "actualTotalTanks",
        fieldValue: currentTotalTanksValue
      })
    );
  };
  const unitFormater = item => {
    let unit;
    if (item === "liter") {
      unit = "L";
    } else if (item === "kilogram") {
      unit = "kg";
    } else if (item === "milliliter") {
      unit = "mL";
    } else {
      unit = "g";
    }
    return unit;
  };

  return (
    <>
      <Header as="h3">Areas Details</Header>
      <Table unstackable className={`${styles.areas} ${styles.sprayTable}`}>
        <Table.Header>
          <tr>
            <th>Status</th>
            <th>Area</th>
            <th>Size</th>
            <th>Volume (L)</th>
            {selectedMachinery && <th>Tanks</th>}
            {selectedMachinery && someDone && <th>Used Volume (L)</th>}
            {selectedMachinery && someDone && <th>Used Tanks</th>}
            {selectedChemicals.length > 0 &&
              selectedChemicals.map(chemical => <th>{chemical.name}</th>)}
            <th className={styles.checkboxHeaderCol} />
          </tr>
        </Table.Header>
        <Table.Body>
          {areasToMap.map(area => {
            const block = new Area(area);
            const variety = block.areaVarietyByDate(plannedDate);
            const varietyList = area.varietiesList;
            return (
              <tr key={area.key}>
                <td>
                  <Checkbox
                    fitted
                    checked={area.done}
                    className="check-box"
                    onChange={handleAreaDoneClick(area.id)}
                  />
                </td>
                <td>
                  <div className={styles.areaName}>{area.name}</div>
                  <div
                    className={`${styles.variety} ${
                      variety.name || varietyList ? "" : styles.vacant
                    }`}
                  >
                    {varietyList
                      ? _.uniq(varietyList).map((vary, index) => {
                          return <div key={`variety_${index}`}>{vary}</div>;
                        })
                      : variety.name
                      ? `${variety.crop ? `${variety.crop.name} - ` : ""}${
                          variety.name
                        }`
                      : "Vacant"}
                  </div>
                </td>
                <td>
                  <Numeric
                    value={area.size}
                    fractionDigits={2}
                    commaSeparatorOnThousands
                    units="Ha"
                  />
                </td>
                <td>
                  <Numeric
                    value={area.plannedLiters}
                    fractionDigits={2}
                    commaSeparatorOnThousands
                    units="L"
                  />
                </td>
                {selectedMachinery && (
                  <td>
                    <Numeric
                      value={area.tanks}
                      fractionDigits={2}
                      commaSeparatorOnThousands
                    />
                  </td>
                )}
                {selectedMachinery && someDone && (
                  <td className={styles.withInput}>
                    <Input
                      fluid
                      key={usedVolumeKey}
                      placeholder={
                        area.tanks &&
                        (area.tanks * machineryLitersSize).toFixed(2)
                      }
                      defaultValue={
                        area.actualTanks !== null &&
                        area.actualTanks !== undefined &&
                        area.actualTanks !== area.tanks
                          ? (area.actualTanks * machineryLitersSize).toFixed(2)
                          : ""
                      }
                      disabled={!area.done}
                      onBlur={() => setUsedVolumeKey(uuid())}
                      onChange={e =>
                        handleUsedVolumeChange(area, e.target.value)
                      }
                    />
                  </td>
                )}
                {selectedMachinery && someDone && (
                  <td className={styles.withInput}>
                    <Input
                      fluid
                      key={usedTanksKey}
                      placeholder={area.tanks && area.tanks.toFixed(2)}
                      defaultValue={
                        area.actualTanks !== null &&
                        area.actualTanks !== undefined &&
                        area.actualTanks !== area.tanks
                          ? parseFloat(area.actualTanks).toFixed(2)
                          : ""
                      }
                      disabled={!area.done}
                      onBlur={() => setUsedTanksKey(uuid())}
                      onChange={e =>
                        handleUsedTanksChange(area, e.target.value)
                      }
                    />
                  </td>
                )}
                {selectedChemicals.length > 0 &&
                  selectedChemicals.map(chemical => {
                    const {
                      quantity: predictedQuantity,
                      units
                    } = calculateChemicalQuantity(
                      chemical,
                      area.size,
                      rowsToSpray,
                      widthPerRow,
                      literPerHectare,
                      conc
                    );

                    let quantity = predictedQuantity;

                    if (
                      area.actualTanks !== null &&
                      area.actualTanks !== undefined &&
                      area.actualTanks !== area.tanks &&
                      area.actualTanks > 0 &&
                      area.tanks > 0
                    ) {
                      quantity =
                        (predictedQuantity * area.actualTanks) / area.tanks;
                    }

                    return (
                      <td>
                        <Numeric
                          fractionDigits={2}
                          value={
                            isFinite(quantity)
                              ? quantity * multiplier(chemical.rate.unit)
                              : "-"
                          }
                          commaSeparatorOnThousands
                          units={unitFormater(chemical.rate.unit)}
                        />
                      </td>
                    );
                  })}
                <td />
              </tr>
            );
          })}
          <tr className={styles.lastRow}>
            <td>
              <Checkbox
                fitted
                checked={selectedAreas.every(area => area.done)}
                onChange={() => dispatch(setAllAreasDoneState(!allDone))}
                className="check-box"
              />
            </td>
            <td>{`${selectedAreas.length} Fields`}</td>
            <td>
              <Numeric
                value={totalHectares}
                fractionDigits={2}
                commaSeparatorOnThousands
                units="Ha"
              />
            </td>
            <td>
              <Numeric
                value={totalVolume}
                fractionDigits={2}
                commaSeparatorOnThousands
                units="L"
              />
            </td>
            {selectedMachinery && (
              <td>
                <Numeric
                  value={totalTanks}
                  fractionDigits={2}
                  commaSeparatorOnThousands
                />
              </td>
            )}
            {selectedMachinery && someDone && (
              <td className={styles.withInput}>
                <Input
                  fluid
                  key={totalUsedVolumeKey}
                  placeholder={totalUsedVolume || totalVolume}
                  defaultValue={
                    totalUsedVolume !== totalVolume ? totalUsedVolume : ""
                  }
                  onBlur={() => setTotalUsedVolumeKey(uuid())}
                  disabled={!allDone}
                  onChange={e => handleTotalUsedVolumeChange(e.target.value)}
                />
              </td>
            )}
            {selectedMachinery && someDone && (
              <td className={styles.withInput}>
                <Input
                  fluid
                  key={totalUsedTanksKey}
                  placeholder={totalUsedTanks || totalTanks.toFixed(2)}
                  defaultValue={
                    totalUsedTanks !== (totalTanks && totalTanks.toFixed(2))
                      ? totalUsedTanks
                      : ""
                  }
                  onBlur={() => setTotalUsedTanksKey(uuid())}
                  disabled={!allDone}
                  onChange={e => handleTotalUsedTanksChange(e.target.value)}
                />
              </td>
            )}

            {selectedChemicals.length > 0 &&
              totalChemicals.map(chemical => (
                <td>
                  <Numeric
                    fractionDigits={2}
                    value={
                      isFinite(chemical.quantity)
                        ? chemical.quantity * multiplier(chemical.units)
                        : "-"
                    }
                    commaSeparatorOnThousands
                    units={unitFormater(chemical.units)}
                  />
                </td>
              ))}
            <td />
          </tr>
        </Table.Body>
      </Table>
      {selectedMachinery && (
        <>
          <div className={styles.tanksAdditionalInfo}>
            {`1 Tank = `}
            <Numeric
              value={machineryLitersSize}
              fractionDigits={2}
              commaSeparatorOnThousands
              units="L"
            />
          </div>
          {totalUsedTanksDecimal !== 0 && (
            <div className={styles.tanksAdditionalInfo}>
              <Numeric
                value={totalUsedTanksDecimal}
                fractionDigits={2}
                commaSeparatorOnThousands
                units="Tank"
              />
              {` = `}
              <Numeric
                value={totalUsedVolume % machineryLitersSize}
                fractionDigits={2}
                commaSeparatorOnThousands
                units="L"
              />
            </div>
          )}
        </>
      )}
    </>
  );
};

SelectedAreasTable.propTypes = {
  actions: PropTypes.object,
  areasList: PropTypes.object,
  plannedDate: PropTypes.object,
  totalTanks: PropTypes.number,
  actualTotalTanks: PropTypes.number,
  sprayDiary: PropTypes.object,
  selectedAreas: PropTypes.array,
  selectedMachinery: PropTypes.number
};

export default memo(SelectedAreasTable);
